import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Container,
  Stack,
  Typography,
  CircularProgress,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Tooltip,
} from '@mui/material'
import { Download, Print as PrintIcon } from '@mui/icons-material'
import { NorthStarSectionInstance, NorthStarSectionTemplate } from '../../services/api'
import LegalFooter from '../LegalFooter'
import { SECTION_RENDERERS } from './sections/registry'

// Inline font + theme styles for the premium aesthetic.
// Loaded inline so the page doesn't depend on the global theme.
const PUBLIC_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Newsreader:opsz,wght@6..72,400;6..72,500;6..72,600;6..72,700&family=Inter:wght@400;500;600&display=swap');

:root {
  --ns-display-font: "Newsreader", "Times New Roman", serif;
}

body.ns-public {
  background: #fbf9f4;
  color: #1a1a1a;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  font-size: 16px;
  line-height: 1.6;
}
body.ns-public .ns-page-root {
  /* overflow-x: clip prevents horizontal bleed from section tints without
     turning this element into a scroll container, so position:sticky on the
     nested AppBar still anchors to the viewport. */
  overflow-x: clip;
}

@media print {
  body[data-print="true"] .ns-no-print { display: none !important; }
  body[data-print="true"] .ns-anchor-nav { display: none !important; }
  body[data-print="true"] .ns-sticky-pdf { display: none !important; }
  body[data-print="true"] { background: white; }
}
`

/** Injects the North Star page fonts and body styles while `enabled`, and removes them after. */
export function useNorthStarPageStyles(enabled = true) {
  useEffect(() => {
    if (!enabled) return
    const style = document.createElement('style')
    style.textContent = PUBLIC_CSS
    document.head.appendChild(style)
    document.body.classList.add('ns-public')
    return () => {
      document.body.classList.remove('ns-public')
      document.head.removeChild(style)
    }
  }, [enabled])
}

interface NorthStarDocumentProps {
  sections: NorthStarSectionInstance[]
  templates: Record<string, NorthStarSectionTemplate>
  clientName: string
  currentRole: string
  publishedAt?: string
  disclaimer?: string
  /** Rendered above the nav bar; hidden in print and PDF output. */
  banner?: ReactNode
  /**
   * Set when the page is shown inside the editor. Nav links scroll in place instead of changing
   * the URL hash, and footer links open in a new tab so a click can't leave the editor and drop
   * unsaved edits.
   */
  previewMode?: boolean
}

/** The client-facing North Star page: nav bar, sections, footer, Print and PDF. */
export default function NorthStarDocument({
  sections,
  templates,
  clientName,
  currentRole,
  publishedAt,
  disclaimer,
  banner,
  previewMode = false,
}: NorthStarDocumentProps) {
  const [downloadingPdf, setDownloadingPdf] = useState(false)
  const pageRef = useRef<HTMLDivElement>(null)

  const enabledSections = useMemo<NorthStarSectionInstance[]>(
    () =>
      sections
        .filter((s) => s.enabled !== false)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [sections]
  )

  const downloadPdf = async () => {
    if (!pageRef.current) return
    setDownloadingPdf(true)
    document.body.setAttribute('data-print', 'true')
    try {
      // Dynamic import keeps the lib out of the initial bundle
      const html2pdf = (await import('html2pdf.js')).default
      const filename = `${(clientName || 'NorthStar').replace(/\s+/g, '-')}-NorthStar.pdf`
      await html2pdf()
        .from(pageRef.current)
        .set({
          margin: 12,
          filename,
          html2canvas: { scale: 2, useCORS: true, logging: false },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        })
        .save()
    } catch (err) {
      console.error('PDF export failed', err)
    } finally {
      document.body.removeAttribute('data-print')
      setDownloadingPdf(false)
    }
  }

  return (
    <Box ref={pageRef} className="ns-page-root">
      {banner}

      <AppBar
        className="ns-anchor-nav ns-no-print"
        position="sticky"
        elevation={0}
        sx={{
          background: 'rgba(251, 249, 244, 0.92)',
          backdropFilter: 'blur(8px)',
          color: '#1a1a1a',
          zIndex: (theme) => theme.zIndex.appBar + 1,
          borderBottom: 1,
          borderColor: 'rgba(0,0,0,0.08)',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 48, sm: 56 } }}>
          <Typography
            sx={{
              fontFamily: 'var(--ns-display-font)',
              fontWeight: 600,
              letterSpacing: '-0.01em',
              fontSize: 16,
              flex: 1,
            }}
          >
            MissFit
          </Typography>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2 }}>
            {enabledSections.slice(1).map((s) => {
              const tpl = templates[s.sectionTypeId]
              if (!tpl) return null
              return (
                <a
                  key={s.sectionTypeId}
                  href={`#${s.sectionTypeId}`}
                  onClick={
                    previewMode
                      ? (e) => {
                          e.preventDefault()
                          document
                            .getElementById(s.sectionTypeId)
                            ?.scrollIntoView({ block: 'start' })
                        }
                      : undefined
                  }
                  style={{
                    color: '#5a5a5a',
                    fontSize: 12,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    textDecoration: 'none',
                  }}
                >
                  {tpl.name}
                </a>
              )
            })}
          </Box>
          <Stack direction="row" spacing={1} sx={{ ml: 2 }}>
            <Tooltip title="Print">
              <IconButton size="small" onClick={() => window.print()} aria-label="Print">
                <PrintIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              startIcon={downloadingPdf ? <CircularProgress size={14} /> : <Download />}
              onClick={downloadPdf}
              disabled={downloadingPdf}
              size="small"
              sx={{ color: '#1a1a1a' }}
            >
              PDF
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ pb: 12 }}>
        {enabledSections.map((section, idx) => {
          const template = templates[section.sectionTypeId]
          if (!template) return null
          const Renderer = SECTION_RENDERERS[template.renderType]
          if (!Renderer) return null
          const resolvedTitle = section.titleOverride?.trim() || template.name
          const resolvedDescription =
            section.descriptionOverride?.trim() ?? template.description ?? ''
          return (
            <Renderer
              key={section.sectionTypeId}
              template={template}
              values={section.fieldValues || {}}
              title={resolvedTitle}
              description={resolvedDescription}
              sectionNumber={idx === 0 ? undefined : idx}
              clientName={clientName}
              currentRole={currentRole}
              publishedAt={publishedAt}
              disclaimer={disclaimer}
            />
          )
        })}

        <Box className="ns-no-print" sx={{ mt: 8, pt: 4, borderTop: 1, borderColor: 'rgba(0,0,0,0.08)', textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Crafted by MissFit Mindset
            {publishedAt && ` · ${new Date(publishedAt).toLocaleDateString()}`}
          </Typography>
          <Box sx={{ mt: 1 }}>
            <a
              href="https://missfitmindset.ai"
              {...(previewMode ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              style={{ color: '#5a5a5a', fontSize: 12, textDecoration: 'none' }}
            >
              missfitmindset.ai
            </a>
          </Box>
          <Box
            onClickCapture={
              previewMode
                ? (e) => {
                    const link = (e.target as HTMLElement).closest('a')
                    if (!link) return
                    e.preventDefault()
                    e.stopPropagation()
                    window.open(link.href, '_blank', 'noopener,noreferrer')
                  }
                : undefined
            }
          >
            <LegalFooter sx={{ mt: 1 }} />
          </Box>
        </Box>
      </Container>
    </Box>
  )
}
